import { useCallback, useEffect, useMemo, useState } from "react";
import { getFolders } from "../../../../services/helpdeskFoldersService";
import { getQuickReplies } from "../../../../services/aiChatService";
import { getAiChannelScope } from "../ai/aiSettingsApi";
import useHelpdeskTeam, {
  displayNameOf,
  userIdOf,
} from "../../../../components/helpdesk/useHelpdeskTeam";

// Catálogos que necesitan los editores de macros y reglas: equipo (para
// "Asignar a"), carpetas, cuentas por canal y respuestas rápidas. Cada fuente
// degrada sola: si una falla, su selector lo dice y el resto del editor sigue
// operable. Las etiquetas NO van acá: salen de useHelpdeskLabels (caché de
// sesión compartida con la bandeja). El equipo sale de useHelpdeskTeam (mismo
// fetch y mismos roles que el selector de agente del chat).

async function loadQuickReplies() {
  const { quick_replies } = await getQuickReplies();
  return (quick_replies || []).filter((r) => r?.active !== false);
}

// Cuentas del alcance de la IA: traen la `key` (evo:/wa:/page:) con la que el
// backend evalúa la condición `account_key` de una regla.
async function loadScopeAccounts() {
  const { channels } = await getAiChannelScope();
  return (Array.isArray(channels) ? channels : []).flatMap((c) =>
    (c.accounts || []).map((a) => ({ key: a.key, label: a.label, phone: a.phone, channel: c.channel })),
  );
}

const LOADERS = {
  folders: getFolders,
  accounts: loadScopeAccounts,
  quickReplies: loadQuickReplies,
};

/**
 * @param {Array<'team'|'folders'|'accounts'|'quickReplies'>} sources
 * @param {boolean} [enabled=true]  false = no pedir nada (p. ej. editor cerrado)
 * @returns {{data: Record<string, Array|null>, errors: Record<string, boolean>, retry: (source:string)=>void}}
 *   `data[source]` es null mientras carga.
 */
export function useWorkflowCatalogs(sources, enabled = true) {
  const wantsTeam = sources.includes("team");
  const { team, error: teamError, reload: reloadTeam } = useHelpdeskTeam({
    enabled: enabled && wantsTeam,
  });
  const [data, setData] = useState(() => ({
    folders: null,
    accounts: null,
    quickReplies: null,
  }));
  const [errors, setErrors] = useState({});
  const key = sources.filter((source) => LOADERS[source]).join(",");

  const load = useCallback((source) => {
    const loader = LOADERS[source];
    if (!loader) return () => {};
    let cancelled = false;
    setErrors((prev) => ({ ...prev, [source]: false }));
    loader()
      .then((list) => {
        if (!cancelled) setData((prev) => ({ ...prev, [source]: Array.isArray(list) ? list : [] }));
      })
      .catch(() => {
        if (cancelled) return;
        setErrors((prev) => ({ ...prev, [source]: true }));
        setData((prev) => ({ ...prev, [source]: prev[source] ?? [] }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const cancels = key.split(",").filter(Boolean).map((source) => load(source));
    return () => cancels.forEach((cancel) => cancel());
  }, [key, enabled, load]);

  const retry = useCallback(
    (source) => (source === "team" ? reloadTeam() : load(source)),
    [load, reloadTeam],
  );

  // Equipo en la forma que usa el editor: { id, name }.
  const teamOptions = useMemo(
    () =>
      team === null
        ? null
        : team.map((u) => ({ id: userIdOf(u), name: displayNameOf(u) })).filter((u) => !!u.id),
    [team],
  );

  return {
    data: { ...data, team: wantsTeam ? teamOptions : null },
    errors: { ...errors, team: wantsTeam && !!teamError },
    retry,
  };
}
